import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Mail, Sparkles } from 'lucide-react';
import type { LegalLinksDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { useLanguage } from '../../lib/i18n';
import { FeatureRequestDialog } from './FeatureRequestDialog';
import { ActionRow, ListCard, SectionTitle } from './settingsParts';

interface HelpSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Ayarlar > Yardım al: destekle iletişim (posta uygulaması, hesap bilgileri
 * dolu) ve özellik isteği (uygulama içi pencere).
 */
export const HelpSection: React.FC<HelpSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const [supportEmail, setSupportEmail] = useState<string | null>(null);
  const [isOpeningMail, setIsOpeningMail] = useState(false);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [legalLinks, setLegalLinks] = useState<LegalLinksDTO | null>(null);

  useEffect(() => {
    window.zabit.getLegalLinks().then(setLegalLinks).catch(() => {});
  }, []);

  const openLink = (url: string) =>
    window.zabit.openExternal(url).catch(() => onNotify('error', t('Bağlantı açılamadı.', 'Could not open the link.')));

  const contactSupport = async () => {
    setIsOpeningMail(true);
    try {
      const result = await window.zabit.contactSupport();
      setSupportEmail(result.email);
      if (!result.opened) {
        onNotify(
          'info',
          t(`Posta uygulaması açılamadı. Adres panoda: ${result.email}`, `Couldn’t open your mail app. The address is on your clipboard: ${result.email}`)
        );
      }
    } catch (err) {
      onNotify('error', (err as Error).message || t('Posta uygulaması açılamadı.', 'Could not open your mail app.'));
    } finally {
      setIsOpeningMail(false);
    }
  };

  // Tanıtım sitesindeki (talknote.net) yasal sayfalar.
  const legalRows = [
    {
      url: legalLinks?.terms,
      title: t('Kullanım Koşulları', 'Terms of Use'),
      description: t(
        'Hizmetin kuralları ve toplantı kaydederken katılımcılara karşı sorumlulukların.',
        'The rules of the service and your responsibilities to participants when recording.'
      ),
    },
    {
      url: legalLinks?.privacy,
      title: t('Gizlilik ve KVKK', 'Privacy (KVKK)'),
      description: t(
        'Hangi verileri, neden ve nerede işlediğimiz; hakların ve başvuru yolu.',
        'What data we process, why and where; your rights and how to use them.'
      ),
    },
    {
      url: legalLinks?.distanceSales,
      title: t('Mesafeli Satış Sözleşmesi', 'Distance Sales Agreement'),
      description: t('Abonelik satın alırken geçerli olan sözleşme.', 'The agreement that applies when you buy a subscription.'),
    },
    {
      url: legalLinks?.delivery,
      title: t('Teslimat ve İade', 'Delivery and Refunds'),
      description: t('Hizmetin teslimi, cayma hakkı ve iade koşulları.', 'Service delivery, the right of withdrawal and refunds.'),
    },
    {
      url: legalLinks?.disclaimer,
      title: t('Sorumluluk Reddi', 'Disclaimer'),
      description: t(
        'Yapay zekâ çıktıları, kayıt ve üçüncü taraf platformlar için sınırlar.',
        'Limits that apply to AI output, recording and third-party platforms.'
      ),
    },
    {
      url: legalLinks?.participants,
      title: t('Toplantı katılımcıları için', 'For meeting participants'),
      description: t(
        'Katılımcılara verebileceğin hazır aydınlatma ve açık rıza örnekleri.',
        'Ready-made notice and consent samples you can give participants.'
      ),
    },
  ].filter((row): row is typeof row & { url: string } => Boolean(row.url));

  return (
    <div className="space-y-8">
      <section className="space-y-2.5">
        <SectionTitle>{t('Destek', 'Support')}</SectionTitle>
        <ListCard>
          <ActionRow
            title={t('Destekle iletişime geç', 'Contact support')}
            description={t(
              'Posta uygulamanı hesap bilgilerin doldurulmuş olarak açar — genellikle bir gün içinde yanıt veriyoruz.',
              'Opens your mail app with your account details filled in — usually a reply within a day.'
            )}
            action={
              <Button variant="secondary" disabled={isOpeningMail} onClick={() => void contactSupport()}>
                {isOpeningMail ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
                {t('Destekle iletişime geç', 'Contact support')}
              </Button>
            }
          />
        </ListCard>
        {supportEmail && (
          <p className="px-1 text-[0.8rem] leading-relaxed text-muted-foreground">
            {t(
              `Posta uygulamanı açtık ve ${supportEmail} adresini yedek olarak panoya kopyaladık.`,
              `We opened your mail app and copied ${supportEmail} to your clipboard as a backup.`
            )}
          </p>
        )}
      </section>

      <section className="space-y-2.5">
        <SectionTitle>{t('Fikirler', 'Ideas')}</SectionTitle>
        <ListCard>
          <ActionRow
            title={t('Özellik iste', 'Request a feature')}
            description={t(
              'Eksik bir şey mi var? Fikrini gönder — her isteği okuyoruz.',
              'Missing something? Send us the idea — we read every request.'
            )}
            action={
              <Button variant="secondary" onClick={() => setIsRequestOpen(true)}>
                <Sparkles className="size-4" />
                {t('Özellik iste', 'Request a feature')}
              </Button>
            }
          />
        </ListCard>
      </section>

      {legalRows.length > 0 && (
        <section className="space-y-2.5">
          <SectionTitle>{t('Yasal', 'Legal')}</SectionTitle>
          <ListCard>
            {legalRows.map((row) => (
              <ActionRow
                key={row.url}
                title={row.title}
                description={row.description}
                action={
                  <Button variant="secondary" onClick={() => void openLink(row.url)}>
                    <ExternalLink className="size-4" />
                    {t('Aç', 'Open')}
                  </Button>
                }
              />
            ))}
          </ListCard>
        </section>
      )}

      <FeatureRequestDialog
        open={isRequestOpen}
        onClose={() => setIsRequestOpen(false)}
        onContactSupport={() => {
          setIsRequestOpen(false);
          void contactSupport();
        }}
        onNotify={onNotify}
      />
    </div>
  );
};
